import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * ThemeContext — Light / Dark / System aware theming.
 *
 * State: "light" | "dark" | "system" (persisted in localStorage under `theme`).
 * When "system", we resolve against `prefers-color-scheme` and live-update if
 * the OS/browser flips it. The resolved mode is written to <html> as both a
 * `class="dark"` (Tailwind darkMode: 'class') and `data-theme="dark|light"`.
 */
const STORAGE_KEY = "theme";
const ThemeCtx = createContext(null);

function systemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readStoredPref() {
  if (typeof window === "undefined") return "system";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

function applyResolvedTheme(mode /* "light" | "dark" */) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = mode;
  root.classList.toggle("dark", mode === "dark");
  root.style.colorScheme = mode; // native form controls, scrollbars
}

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(readStoredPref);
  const [resolved, setResolved] = useState(() =>
    (pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref)
  );

  // Apply the resolved theme to <html> whenever it changes.
  useEffect(() => { applyResolvedTheme(resolved); }, [resolved]);

  // Persist the user pref + recompute the resolved theme.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, pref); } catch {}
    setResolved(pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref);
  }, [pref]);

  // Live-follow the OS setting when the user pref is "system".
  useEffect(() => {
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setResolved(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [pref]);

  const setTheme = useCallback((next) => {
    if (next === "light" || next === "dark" || next === "system") setPref(next);
  }, []);

  const cycleTheme = useCallback(() => {
    // Sun → Moon → Monitor → Sun (light → dark → system → light).
    setPref((cur) => (cur === "light" ? "dark" : cur === "dark" ? "system" : "light"));
  }, []);

  return (
    <ThemeCtx.Provider value={{ pref, resolved, setTheme, cycleTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error("useTheme must be used within a ThemeProvider");
  return v;
}
